import React from 'react';
import { Container, Typography, Paper, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function MuiRhfAdvanced3Docs() {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          MUI + RHF Advanced - Part 3
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          🏠 HOME
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        에러 처리와 성능 최적화를 통해 폼의 완성도를 높여보겠습니다. 
        실무에서 만날 수 있는 다양한 상황에 대비하고, 사용자 경험을 개선하는 방법을 학습합니다.
      </Typography>

      <Typography variant="h4" gutterBottom sx={{ mt: 4, mb: 2 }}>
        에러 처리 및 최적화
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        고급 유효성 검사
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        복잡한 비즈니스 로직을 포함한 유효성 검사와 비동기 검증을 구현하는 방법입니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

// 복잡한 유효성 검사 스키마
const productSchema = yup.object({
  name: yup.string()
    .required('상품명은 필수입니다')
    .min(2, '최소 2글자 이상')
    .max(50, '최대 50글자까지')
    .test('unique-name', '이미 존재하는 상품명입니다', async (value) => {
      if (!value) return true;
      const response = await fetch(\`/api/products/check-name?name=\${value}\`);
      const data = await response.json();
      return !data.exists;
    }),
    
  price: yup.number()
    .required('가격은 필수입니다')
    .min(100, '최소 100원 이상')
    .max(10000000, '최대 1000만원까지')
    .test('price-increment', '100원 단위로 입력해주세요', (value) => {
      return value ? value % 100 === 0 : true;
    }),
    
  category: yup.string().required('카테고리를 선택해주세요'),
  
  stock: yup.number()
    .required('재고는 필수입니다')
    .min(0, '0 이상이어야 합니다')
    .integer('정수만 입력 가능합니다'),
    
  // 조건부 유효성 검사
  discountPrice: yup.number()
    .when('isOnSale', {
      is: true,
      then: yup.number()
        .required('할인가는 필수입니다')
        .test('discount-valid', '할인가는 정가보다 낮아야 합니다', 
          function(value) {
            const { price } = this.parent;
            return !value || !price || value < price;
          }
        ),
      otherwise: yup.number().nullable()
    }),
    
  // 배열 유효성 검사
  tags: yup.array()
    .min(1, '최소 1개의 태그를 입력해주세요')
    .max(5, '최대 5개까지 입력 가능합니다')
    .of(yup.string().min(2, '태그는 최소 2글자 이상'))
});

function AdvancedValidationForm() {
  const { control, handleSubmit, setError, formState: { errors, isValidating } } = useForm({
    resolver: yupResolver(productSchema),
    mode: 'onBlur'  // 포커스 아웃 시 검증
  });

  const onSubmit = async (data) => {
    try {
      // 서버 사이드 검증
      const response = await fetch('/api/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // 서버 에러를 폼 에러로 설정
        if (errorData.fieldErrors) {
          Object.entries(errorData.fieldErrors).forEach(([field, message]) => {
            setError(field, { type: 'server', message });
          });
        } else {
          // 일반적인 서버 에러
          setError('root.serverError', {
            type: 'server',
            message: errorData.message || '서버 오류가 발생했습니다'
          });
        }
        return;
      }

      toast.success('상품이 등록되었습니다!');
    } catch (error) {
      setError('root.serverError', {
        type: 'network',
        message: '네트워크 오류가 발생했습니다'
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* 서버 에러 표시 */}
      {errors.root?.serverError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.root.serverError.message}
        </Alert>
      )}

      {/* 비동기 검증 진행 표시 */}
      {isValidating && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      <Controller
        name="name"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label="상품명"
            fullWidth
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
    </form>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        성능 최적화 기법
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        대용량 폼이나 복잡한 상태 관리에서 성능을 최적화하는 방법들입니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { memo, useCallback, useMemo } from 'react';
import { debounce } from 'lodash';

// 메모이제이션으로 불필요한 리렌더링 방지
const OptimizedField = memo(({ name, control, ...props }) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          {...props}
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  );
});

// 디바운스를 사용한 실시간 검색
function SearchableSelect({ name, control, searchFn }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!query) {
        setOptions([]);
        return;
      }
      
      setLoading(true);
      try {
        const results = await searchFn(query);
        setOptions(results);
      } catch (error) {
        console.error('검색 실패:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    [searchFn]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          options={options}
          loading={loading}
          value={value}
          onChange={(_, newValue) => onChange(newValue)}
          onInputChange={(_, newInputValue) => {
            debouncedSearch(newInputValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="검색"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
      )}
    />
  );
}

// 가상화를 사용한 대용량 리스트
import { FixedSizeList as List } from 'react-window';

function VirtualizedFieldArray({ control, name }) {
  const { fields } = useFieldArray({ control, name });

  const Row = useCallback(({ index, style }) => (
    <div style={style}>
      <Paper sx={{ p: 1, m: 0.5 }}>
        <OptimizedField
          name={\`\${name}.\${index}.value\`}
          control={control}
          label={\`항목 \${index + 1}\`}
        />
      </Paper>
    </div>
  ), [control, name]);

  if (fields.length === 0) {
    return <Typography>항목이 없습니다</Typography>;
  }

  return (
    <List
      height={400}
      itemCount={fields.length}
      itemSize={80}
    >
      {Row}
    </List>
  );
}

// 조건부 렌더링 최적화
function ConditionalFields({ control, watch }) {
  const formType = watch('type');
  
  // 조건부 컴포넌트를 메모이제이션
  const PhysicalProductFields = useMemo(() => (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <OptimizedField
          name="weight"
          control={control}
          label="무게(kg)"
          type="number"
        />
      </Grid>
      <Grid item xs={6}>
        <OptimizedField
          name="dimensions"
          control={control}
          label="크기(cm)"
        />
      </Grid>
    </Grid>
  ), [control]);

  const DigitalProductFields = useMemo(() => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <OptimizedField
          name="downloadUrl"
          control={control}
          label="다운로드 URL"
          type="url"
        />
      </Grid>
      <Grid item xs={6}>
        <OptimizedField
          name="fileSize"
          control={control}
          label="파일 크기(MB)"
          type="number"
        />
      </Grid>
    </Grid>
  ), [control]);

  return (
    <Box>
      {formType === 'physical' && PhysicalProductFields}
      {formType === 'digital' && DigitalProductFields}
    </Box>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        에러 바운더리와 복구
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        예상치 못한 에러로부터 폼을 보호하고 사용자에게 적절한 피드백을 제공하는 방법입니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { ErrorBoundary } from 'react-error-boundary';

function FormErrorFallback({ error, resetErrorBoundary }) {
  return (
    <Alert 
      severity="error" 
      action={
        <Button onClick={resetErrorBoundary}>
          다시 시도
        </Button>
      }
    >
      <AlertTitle>폼 오류 발생</AlertTitle>
      문제가 발생했습니다. 페이지를 새로고침하거나 다시 시도해주세요.
      {process.env.NODE_ENV === 'development' && (
        <details style={{ marginTop: 8 }}>
          <summary>기술적 상세사항</summary>
          <pre style={{ fontSize: '12px', marginTop: 4 }}>
            {error.message}
          </pre>
        </details>
      )}
    </Alert>
  );
}

function RobustForm() {
  const { control, reset, formState: { errors } } = useForm();
  const [retryCount, setRetryCount] = useState(0);

  const handleFormError = useCallback((error, errorInfo) => {
    // 에러 로깅
    console.error('Form Error:', error, errorInfo);
    
    // 외부 에러 추적 서비스로 전송
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.message,
        fatal: false
      });
    }
  }, []);

  const handleRetry = useCallback(() => {
    setRetryCount(prev => prev + 1);
    reset();  // 폼 상태 초기화
  }, [reset]);

  return (
    <ErrorBoundary
      FallbackComponent={FormErrorFallback}
      onError={handleFormError}
      onReset={handleRetry}
      resetKeys={[retryCount]}  // 이 값이 변경되면 에러 바운더리 리셋
    >
      <form>
        {/* 네트워크 상태 표시 */}
        <NetworkStatusIndicator />
        
        {/* 자동 저장 기능 */}
        <AutoSaveIndicator control={control} />
        
        {/* 실제 폼 필드들 */}
        <ProductFormFields control={control} />
        
        {/* 제출 버튼 */}
        <SubmitButton errors={errors} />
      </form>
    </ErrorBoundary>
  );
}

// 자동 저장 기능
function AutoSaveIndicator({ control }) {
  const [lastSaved, setLastSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const formValues = watch({ control });
  
  const debouncedSave = useCallback(
    debounce(async (data) => {
      setSaving(true);
      try {
        await localStorage.setItem('form-draft', JSON.stringify(data));
        setLastSaved(new Date());
      } catch (error) {
        console.error('자동 저장 실패:', error);
      } finally {
        setSaving(false);
      }
    }, 2000),
    []
  );
  
  useEffect(() => {
    debouncedSave(formValues);
  }, [formValues, debouncedSave]);
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      {saving ? (
        <>
          <CircularProgress size={16} />
          <Typography variant="caption">저장 중...</Typography>
        </>
      ) : lastSaved ? (
        <>
          <CheckCircle size={16} color="success" />
          <Typography variant="caption">
            마지막 저장: {lastSaved.toLocaleTimeString()}
          </Typography>
        </>
      ) : null}
    </Box>
  );
}`}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/docs/mui-rhf-advanced-2')}
        >
          ← PART 2: 고급 컴포넌트 연동
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/docs/graphql-basics-1')}
        >
          다음: GraphQL 학습 →
        </Button>
      </Box>
    </Container>
  );
}
